import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiPauseCircleBold, PiPlusBold, PiTrashBold } from 'react-icons/pi';
import { Badge, Button, Skeleton } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';

export type DealActionKey = 'return' | 'transfer' | 'hold' | 'shift' | 'wallet' | 'reschedule' | 'history';

type EditableInstallment = {
    id: number;
    installment_number: number;
    due_date: string | null;
    amount: number;
    status: string;
    locked: boolean;
    competence: string | null;
    competence_override: string | null;
};

type DealActionState = {
    locked_at: string | null;
    lock_reason: string | null;
    hold_from: string | null;
    hold_reason: string | null;
    amount: number;
    adjusted_amount: number | null;
    installments: EditableInstallment[];
};

type Person = { id: number; name: string; email?: string | null };

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

const inputClass = 'mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900';

function fmtMonth(ym?: string | null) {
    if (!ym) return '—';
    const [y, m] = ym.split('-').map(Number);
    const name = new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'long' });
    return `${name.charAt(0).toUpperCase()}${name.slice(1)}/${y}`;
}

function currentMonth() {
    return new Date().toISOString().slice(0, 7);
}

function apiMessage(err: unknown): string | null {
    const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
    return Object.values(data?.errors ?? {})[0]?.[0] ?? data?.message ?? null;
}

export function useDealActionState(dealId: number | string, enabled: boolean) {
    return useQuery({
        queryKey: ['commissioning', 'deal-actions', dealId],
        queryFn: () => api.get(`/commissioning/deals/${dealId}/actions/state`).then((r) => r.data.data as DealActionState),
        enabled,
        retry: false,
    });
}

/** Selos no título do card: ajuste manual e suspensão. */
export function DealActionBadges({ dealId, enabled }: { dealId: number | string; enabled: boolean }) {
    const { t } = useI18n();
    const state = useDealActionState(dealId, enabled).data;
    if (!state) return null;

    return (
        <>
            {state.hold_from && (
                <Badge color="warning" variant="flat" rounded="pill" size="sm" title={state.hold_reason ?? ''}>
                    <PiPauseCircleBold className="mr-1 size-3" aria-hidden />
                    {t('v2.deal_actions.badge_hold', { month: fmtMonth(state.hold_from) })}
                </Badge>
            )}
            {state.locked_at && (
                <Badge color="secondary" variant="flat" rounded="pill" size="sm" title={state.lock_reason ?? ''}>
                    {t('v2.deal_actions.badge_locked', { reason: state.lock_reason ?? '' })}
                </Badge>
            )}
        </>
    );
}

export default function DealCommissionActions({
    dealId,
    action,
    onClose,
}: {
    dealId: number | string;
    action: DealActionKey | null;
    onClose: () => void;
}) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const stateQuery = useDealActionState(dealId, action !== null);
    const state = stateQuery.data;
    const [reason, setReason] = useState('');

    useEffect(() => {
        setReason('');
    }, [action]);

    const refresh = () => {
        void qc.invalidateQueries({ queryKey: ['commissioning', 'deal-actions', dealId] });
        void invalidateDealCommissionQueries(qc, dealId);
    };

    const run = useMutation({
        mutationFn: ({ path, body }: { path: string; body: Record<string, unknown> }) =>
            api.post(`/commissioning/deals/${dealId}/actions/${path}`, { ...body, reason: reason.trim() }),
        onSuccess: (res) => {
            toast.success(res.data?.message ?? t('v2.deal_actions.done'));
            refresh();
            onClose();
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.deal_actions.failed')),
    });

    const reasonField = (
        <label className="block text-xs font-semibold text-gray-600">
            {t('v2.deal_actions.reason')}
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={inputClass} />
        </label>
    );
    const reasonOk = reason.trim().length >= 3;

    const footer = (onConfirm: () => void, label: string, disabled = false, danger = false) => (
        <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('v2.deal_actions.cancel')}</Button>
            <Button type="button" color={danger ? 'danger' : 'primary'} isLoading={run.isPending} disabled={disabled || !reasonOk} onClick={onConfirm}>
                {label}
            </Button>
        </div>
    );

    if (action === 'history') {
        return <HistoryModal dealId={dealId} onClose={onClose} />;
    }

    if (action === null) return null;

    const loading = stateQuery.isLoading || !state;

    return (
        <>
            {action === 'return' && (
                <ReturnModal state={state} loading={loading} reasonField={reasonField} footer={footer} onClose={onClose}
                    onConfirm={(returned) => run.mutate({ path: 'partial-return', body: { returned_amount: returned } })} />
            )}
            {action === 'transfer' && (
                <TransferModal dealId={dealId} reasonField={reasonField} footer={footer} onClose={onClose}
                    onConfirm={(sellerId, mode) => run.mutate({ path: 'transfer', body: { seller_user_id: sellerId, mode } })} />
            )}
            {action === 'hold' && (
                <V2Modal
                    open
                    onClose={onClose}
                    title={state?.hold_from ? t('v2.deal_actions.release_title') : t('v2.deal_actions.hold_title')}
                    description={state?.hold_from
                        ? t('v2.deal_actions.release_hint', { month: fmtMonth(state.hold_from) })
                        : t('v2.deal_actions.hold_hint')}
                    size="md"
                    footer={footer(
                        () => run.mutate({ path: 'hold', body: { release: !!state?.hold_from } }),
                        state?.hold_from ? t('v2.deal_actions.release_confirm') : t('v2.deal_actions.hold_confirm'),
                        loading,
                    )}
                >
                    {loading ? <Skeleton className="h-16 w-full" /> : (
                        <div className="space-y-3">
                            {state?.hold_reason && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{state.hold_reason}</p>}
                            {reasonField}
                        </div>
                    )}
                </V2Modal>
            )}
            {action === 'shift' && (
                <ShiftModal state={state} loading={loading} reasonField={reasonField} footer={footer} onClose={onClose}
                    onConfirm={(installmentId, competence) => run.mutate({ path: `installments/${installmentId}/shift`, body: { competence } })} />
            )}
            {action === 'wallet' && (
                <WalletEntryModal dealId={dealId} reasonField={reasonField} footer={footer} onClose={onClose}
                    onConfirm={(body) => run.mutate({ path: 'wallet-entry', body })} />
            )}
            {action === 'reschedule' && (
                <RescheduleModal state={state} loading={loading} reasonField={reasonField} footer={footer} onClose={onClose}
                    onConfirm={(rows) => run.mutate({ path: 'reschedule', body: { rows } })} />
            )}
        </>
    );
}

type FooterFn = (onConfirm: () => void, label: string, disabled?: boolean, danger?: boolean) => ReactElement;

function ReturnModal({ state, loading, reasonField, footer, onClose, onConfirm }: {
    state?: DealActionState; loading: boolean; reasonField: ReactElement; footer: FooterFn; onClose: () => void; onConfirm: (v: number) => void;
}) {
    const { t } = useI18n();
    const [value, setValue] = useState('');
    const current = state ? (state.adjusted_amount ?? state.amount) : 0;
    const returned = Number(value.replace(/\./g, '').replace(',', '.')) || 0;

    return (
        <V2Modal open onClose={onClose} title={t('v2.deal_actions.return_title')} description={t('v2.deal_actions.return_hint')} size="md"
            footer={footer(() => onConfirm(returned), t('v2.deal_actions.return_confirm'), loading || returned <= 0 || returned >= current)}>
            {loading ? <Skeleton className="h-24 w-full" /> : (
                <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-zinc-800/60">
                            <p className="text-[10px] font-semibold uppercase text-gray-500">{t('v2.deal_actions.current_value')}</p>
                            <p className="text-sm font-bold tabular-nums">{brl(current)}</p>
                        </div>
                        <div className="rounded-lg bg-emerald-50 px-3 py-2 dark:bg-emerald-950/30">
                            <p className="text-[10px] font-semibold uppercase text-emerald-700">{t('v2.deal_actions.new_value')}</p>
                            <p className="text-sm font-bold tabular-nums text-emerald-800">{brl(Math.max(0, current - returned))}</p>
                        </div>
                    </div>
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.deal_actions.returned_amount')}
                        <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} className={inputClass} />
                    </label>
                    {reasonField}
                </div>
            )}
        </V2Modal>
    );
}

function TransferModal({ dealId, reasonField, footer, onClose, onConfirm }: {
    dealId: number | string; reasonField: ReactElement; footer: FooterFn; onClose: () => void; onConfirm: (id: number, mode: 'keep' | 'clawback') => void;
}) {
    const { t } = useI18n();
    const [q, setQ] = useState('');
    const [picked, setPicked] = useState<Person | null>(null);
    const [mode, setMode] = useState<'keep' | 'clawback'>('keep');
    const search = useQuery({
        queryKey: ['commissioning', 'deal-actions', dealId, 'participants', q],
        queryFn: () => api.get(`/commissioning/deals/${dealId}/actions/participants`, { params: { q } })
            .then((r) => r.data.data as { search: Person[] }),
        enabled: q.trim().length >= 2,
    });

    return (
        <V2Modal open onClose={onClose} title={t('v2.deal_actions.transfer_title')} description={t('v2.deal_actions.transfer_hint')} size="md"
            footer={footer(() => picked && onConfirm(picked.id, mode), t('v2.deal_actions.transfer_confirm'), !picked)}>
            <div className="space-y-3">
                {picked ? (
                    <div className="flex items-center justify-between rounded-lg border border-primary bg-primary/10 px-3 py-2 text-sm">
                        <span className="font-semibold">{picked.name}</span>
                        <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setPicked(null)}>{t('v2.deal_actions.change')}</button>
                    </div>
                ) : (
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.deal_actions.new_seller')}
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('v2.deal_actions.search_user')} className={inputClass} />
                        {(search.data?.search ?? []).length > 0 && (
                            <ul className="mt-1 max-h-40 overflow-auto rounded-lg border border-gray-100 dark:border-zinc-800">
                                {search.data!.search.map((u) => (
                                    <li key={u.id}>
                                        <button type="button" onClick={() => setPicked(u)} className="w-full px-3 py-1.5 text-left text-sm font-normal hover:bg-gray-50 dark:hover:bg-zinc-800">
                                            {u.name} <span className="text-xs text-gray-400">{u.email}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </label>
                )}
                <fieldset className="space-y-2">
                    <legend className="text-xs font-semibold text-gray-600">{t('v2.deal_actions.transfer_paid')}</legend>
                    {(['keep', 'clawback'] as const).map((m) => (
                        <label key={m} className={cn('flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-xs', mode === m ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-zinc-700')}>
                            <input type="radio" checked={mode === m} onChange={() => setMode(m)} className="mt-0.5" />
                            <span>
                                <span className="block font-semibold text-gray-900 dark:text-zinc-100">{t(`v2.deal_actions.transfer_mode_${m}`)}</span>
                                <span className="text-gray-500">{t(`v2.deal_actions.transfer_mode_${m}_hint`)}</span>
                            </span>
                        </label>
                    ))}
                </fieldset>
                {reasonField}
            </div>
        </V2Modal>
    );
}

function ShiftModal({ state, loading, reasonField, footer, onClose, onConfirm }: {
    state?: DealActionState; loading: boolean; reasonField: ReactElement; footer: FooterFn; onClose: () => void; onConfirm: (id: number, competence: string | null) => void;
}) {
    const { t } = useI18n();
    const [installmentId, setInstallmentId] = useState<number | null>(null);
    const [month, setMonth] = useState('');
    const options = (state?.installments ?? []).filter((i) => i.status !== 'cancelled');
    const picked = options.find((i) => i.id === installmentId);

    return (
        <V2Modal open onClose={onClose} title={t('v2.deal_actions.shift_title')} description={t('v2.deal_actions.shift_hint')} size="md"
            footer={footer(() => installmentId && onConfirm(installmentId, month || null), t('v2.deal_actions.shift_confirm'), !installmentId)}>
            {loading ? <Skeleton className="h-24 w-full" /> : (
                <div className="space-y-3">
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.deal_actions.installment')}
                        <select value={installmentId ?? ''} onChange={(e) => setInstallmentId(e.target.value ? Number(e.target.value) : null)} className={inputClass}>
                            <option value="">—</option>
                            {options.map((i) => (
                                <option key={i.id} value={i.id}>
                                    {i.installment_number === 0 ? 'Entrada' : `${i.installment_number}ª`} · {brl(i.amount)} · {t('v2.deal_actions.commission_in', { month: fmtMonth(i.competence) })}
                                    {i.competence_override ? ' ✱' : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.deal_actions.new_month')}
                        <input type="month" value={month} min={currentMonth()} onChange={(e) => setMonth(e.target.value)} className={inputClass} />
                        <span className="mt-1 block font-normal text-gray-500">{t('v2.deal_actions.shift_clear_hint')}</span>
                    </label>
                    {picked?.competence_override && (
                        <p className="text-xs text-amber-700">{t('v2.deal_actions.shift_current_override', { month: fmtMonth(picked.competence_override) })}</p>
                    )}
                    {reasonField}
                </div>
            )}
        </V2Modal>
    );
}

function WalletEntryModal({ dealId, reasonField, footer, onClose, onConfirm }: {
    dealId: number | string; reasonField: ReactElement; footer: FooterFn; onClose: () => void; onConfirm: (body: Record<string, unknown>) => void;
}) {
    const { t } = useI18n();
    const [sellerId, setSellerId] = useState<number | null>(null);
    const [kind, setKind] = useState<'debit' | 'credit'>('debit');
    const [amount, setAmount] = useState('');
    const [month, setMonth] = useState(currentMonth());
    const people = useQuery({
        queryKey: ['commissioning', 'deal-actions', dealId, 'participants', ''],
        queryFn: () => api.get(`/commissioning/deals/${dealId}/actions/participants`).then((r) => r.data.data as { deal_users: Person[] }),
    });
    const value = Number(amount.replace(/\./g, '').replace(',', '.')) || 0;

    return (
        <V2Modal open onClose={onClose} title={t('v2.deal_actions.wallet_title')} description={t('v2.deal_actions.wallet_hint')} size="md"
            footer={footer(() => onConfirm({ seller_user_id: sellerId, kind, amount: value, competence: month }), t('v2.deal_actions.wallet_confirm'), !sellerId || value <= 0, kind === 'debit')}>
            <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-xs font-semibold text-gray-600 sm:col-span-2">
                    {t('v2.deal_actions.participant')}
                    <select value={sellerId ?? ''} onChange={(e) => setSellerId(e.target.value ? Number(e.target.value) : null)} className={inputClass}>
                        <option value="">—</option>
                        {(people.data?.deal_users ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </label>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.deal_actions.kind')}
                    <select value={kind} onChange={(e) => setKind(e.target.value as 'debit' | 'credit')} className={inputClass}>
                        <option value="debit">{t('v2.deal_actions.kind_debit')}</option>
                        <option value="credit">{t('v2.deal_actions.kind_credit')}</option>
                    </select>
                </label>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.deal_actions.amount')}
                    <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
                </label>
                <label className="block text-xs font-semibold text-gray-600 sm:col-span-2">
                    {t('v2.deal_actions.competence')}
                    <input type="month" value={month} min={currentMonth()} onChange={(e) => setMonth(e.target.value)} className={inputClass} />
                </label>
                <div className="sm:col-span-2">{reasonField}</div>
            </div>
        </V2Modal>
    );
}

type Row = { key: string; id: number | null; due_date: string; amount: string };

function RescheduleModal({ state, loading, reasonField, footer, onClose, onConfirm }: {
    state?: DealActionState; loading: boolean; reasonField: ReactElement; footer: FooterFn; onClose: () => void;
    onConfirm: (rows: Array<{ id: number | null; due_date: string; amount: number }>) => void;
}) {
    const { t } = useI18n();
    const locked = (state?.installments ?? []).filter((i) => i.locked);
    const editable = useMemo(() => (state?.installments ?? []).filter((i) => !i.locked), [state]);
    const [rows, setRows] = useState<Row[]>([]);

    useEffect(() => {
        setRows(editable.map((i) => ({ key: String(i.id), id: i.id, due_date: i.due_date ?? '', amount: String(i.amount) })));
    }, [editable]);

    const toNumber = (v: string) => Number(v.replace(/\./g, '').replace(',', '.')) || 0;
    const openBalance = editable.reduce((acc, i) => acc + i.amount, 0);
    const newTotal = rows.reduce((acc, r) => acc + toNumber(r.amount), 0);
    const diff = Math.round((newTotal - openBalance) * 100) / 100;
    const valid = rows.length > 0 && Math.abs(diff) <= 0.05 && rows.every((r) => r.due_date && toNumber(r.amount) > 0);

    const update = (key: string, patch: Partial<Row>) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

    return (
        <V2Modal open onClose={onClose} title={t('v2.deal_actions.reschedule_title')} description={t('v2.deal_actions.reschedule_hint')} size="lg"
            footer={footer(() => onConfirm(rows.map((r) => ({ id: r.id, due_date: r.due_date, amount: toNumber(r.amount) }))), t('v2.deal_actions.reschedule_confirm'), !valid)}>
            {loading ? <Skeleton className="h-40 w-full" /> : (
                <div className="space-y-3">
                    {locked.length > 0 && (
                        <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-zinc-800/60 dark:text-zinc-300">
                            <p className="font-semibold">{t('v2.deal_actions.reschedule_locked_rows', { count: locked.length })}</p>
                            <p>{locked.map((i) => `${i.installment_number === 0 ? 'Entrada' : `${i.installment_number}ª`} ${brl(i.amount)}`).join(' · ')}</p>
                        </div>
                    )}
                    <table className="w-full text-left text-xs">
                        <thead className="text-[10px] uppercase text-gray-500">
                            <tr>
                                <th className="py-1 pr-2">{t('v2.deal_actions.due_date')}</th>
                                <th className="py-1 pr-2 text-right">{t('v2.deal_actions.amount')}</th>
                                <th className="py-1" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.key}>
                                    <td className="py-1 pr-2"><input type="date" value={r.due_date} onChange={(e) => update(r.key, { due_date: e.target.value })} className={inputClass} /></td>
                                    <td className="py-1 pr-2"><input inputMode="decimal" value={r.amount} onChange={(e) => update(r.key, { amount: e.target.value })} className={cn(inputClass, 'text-right')} /></td>
                                    <td className="py-1 text-right">
                                        <button type="button" aria-label={t('v2.deal_actions.remove_row')} onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))} className="rounded p-1 text-gray-400 hover:text-rose-600">
                                            <PiTrashBold className="size-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => setRows((prev) => [...prev, { key: `new-${Date.now()}`, id: null, due_date: '', amount: '' }])}>
                            <PiPlusBold className="mr-1 size-3.5" aria-hidden /> {t('v2.deal_actions.add_row')}
                        </Button>
                        <p className={cn('text-xs font-semibold tabular-nums', Math.abs(diff) <= 0.05 ? 'text-emerald-700' : 'text-rose-600')}>
                            {t('v2.deal_actions.reschedule_balance', { open: brl(openBalance), total: brl(newTotal) })}
                        </p>
                    </div>
                    {reasonField}
                </div>
            )}
        </V2Modal>
    );
}

function HistoryModal({ dealId, onClose }: { dealId: number | string; onClose: () => void }) {
    const { t } = useI18n();
    const history = useQuery({
        queryKey: ['commissioning', 'deal-actions', dealId, 'history'],
        queryFn: () => api.get(`/commissioning/deals/${dealId}/actions/history`)
            .then((r) => r.data.data as Array<{ id: number; action: string; description?: string | null; changed_by?: string | null; created_at?: string | null }>),
    });

    return (
        <V2Modal open onClose={onClose} title={t('v2.deal_actions.history_title')} size="lg">
            {history.isLoading ? <Skeleton className="h-40 w-full" /> : (history.data ?? []).length === 0 ? (
                <p className="py-6 text-center text-xs text-gray-500">{t('v2.deal_actions.history_empty')}</p>
            ) : (
                <ol className="relative max-h-[60vh] space-y-3 overflow-auto border-l border-gray-200 pl-4 dark:border-zinc-700">
                    {history.data!.map((h) => (
                        <li key={h.id} className="text-xs">
                            <span className="absolute -left-1.5 mt-1 size-3 rounded-full border-2 border-white bg-primary dark:border-zinc-900" />
                            <p className="font-semibold text-gray-900 dark:text-zinc-100">{h.description ?? h.action}</p>
                            <p className="text-gray-500">
                                {h.created_at ? new Date(h.created_at).toLocaleString('pt-BR') : '—'}
                                {h.changed_by ? ` · ${h.changed_by}` : ''}
                                <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] text-gray-500 dark:bg-zinc-800">{h.action}</span>
                            </p>
                        </li>
                    ))}
                </ol>
            )}
        </V2Modal>
    );
}
